// Central Auth IDP sign-in helpers. The backend (/api/user/sso/*) owns the
// IDP configuration; see docgrid_backend/apps/controllers/DocGridSsoController.js.
import axios from "axios";
import { DocBaseUrl } from "./configurable";

// Set when the current session came from the central login, so "Logout"
// also signs the user out of the IDP and every other product.
const SSO_SESSION_KEY = "docGridSsoSession";

export const ssoLoginUrl = `${DocBaseUrl}/sso/login`;
export const ssoLogoutUrl = `${DocBaseUrl}/sso/logout`;
export const ssoChangePasswordUrl = `${DocBaseUrl}/sso/password/change`;
export const ssoAppsUrl = `${DocBaseUrl}/sso/apps`;
export const ssoNoAccessUrl = `${DocBaseUrl}/sso/no-access`;

export const markSsoSession = () => localStorage.setItem(SSO_SESSION_KEY, "1");
export const isSsoSession = () => localStorage.getItem(SSO_SESSION_KEY) === "1";
export const clearSsoSession = () => localStorage.removeItem(SSO_SESSION_KEY);

// These calls deliberately bypass useFetch: its 401 handler would log out and
// redirect in the middle of a sign-in or logout.
export const fetchAuthMode = async () => {
  try {
    const response = await axios.get(`${DocBaseUrl}/sso/config`);
    return response?.data?.data?.authMode === "idp" ? "idp" : "native";
  } catch (_error) {
    return "native";
  }
};

export const exchangeSsoCode = (code) => axios.post(`${DocBaseUrl}/sso/exchange`, { code });

export const resolveSloNext = async (next) => {
  try {
    const response = await axios.get(`${DocBaseUrl}/sso/slo-next`, { params: { next } });
    return response?.data?.data?.url || null;
  } catch (_error) {
    return null;
  }
};

// authError codes set by the backend callback (and by SsoComplete).
const AUTH_ERROR_MESSAGES = {
  user_not_found:
    "Your account is not set up in DocGrid yet. Ask your DocGrid administrator to add you.",
  user_inactive: "Your DocGrid account is inactive.",
  provisioning_pending:
    "Your DocGrid account is still being set up. Ask your DocGrid administrator to add you again.",
  malformed_tenant: "Your organisation is not connected to DocGrid in the central login.",
  tenant_not_found: "Your organisation is not active in DocGrid.",
  duplicate_email: "More than one DocGrid account uses your email. Contact your administrator.",
  already_linked: "This DocGrid account is linked to a different central login.",
  session_ended:
    "Your DocGrid session has ended, for example because you signed in on another device or your account was deactivated. Sign in again to continue.",
};

// authError codes after which the login page still sends the user straight
// to the IDP in idp mode (the session ended or the user signed out; nothing
// failed). Any other code is a failed sign-in, which stops the redirect so
// it cannot loop between this page and the IDP (same as BidGrid).
export const isSsoAutoRedirectError = (code) =>
  !code || code === "session_ended" || code === "logged_out";

// authError codes meaning the user has no active DocGrid account: user_inactive
// (set inactive while signed in) and user_not_found (the sign-in after that,
// since inactive users are not matched). idp mode shows Auth's Access Denied
// page for these instead of this login page.
export const isSsoNoAccessError = (code) => code === "user_inactive" || code === "user_not_found";

export const getAuthErrorMessage = (code) => {
  if (!code || code === "logged_out") return "";
  return AUTH_ERROR_MESSAGES[code] || "Central sign-in could not be completed. Please try again.";
};
